import { useQuery } from '@tanstack/react-query';
import { useParams } from '@/providers/RouterShim';
import type { ApiResponse } from '../../@types/ApiResponse';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import type { VehicleAdResponse } from '../vehicles/useVehicle';

/**
 * Custom hook to fetch vehicle data for drawer view
 * Fetches vehicle information from REST API
 *
 * @param id - Vehicle/Item ID
 * @returns Tuple of [vehicle data, request state]
 */
export const useVehicleDrawer = (
  id: string | undefined,
): [VehicleAdResponse | undefined, RequestStateQuery] => {
  const { branchId } = useParams();
  const { getToken } = useAuth();

  const { data, isPending, isSuccess, isError } = useQuery<ApiResponse<VehicleAdResponse>>({
    queryKey: ['vehicleDrawer', branchId, id],
    queryFn: async () => {
      if (!id) throw new Error('Vehicle ID is required');
      return await getJSON<ApiResponse<VehicleAdResponse>>(
        `/api/vehicle/${branchId}/${id}/item`,
        getToken()!,
      );
    },
    enabled: !!id && !!branchId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });

  return [
    data?.response,
    {
      isLoading: isPending,
      isSuccess,
      isError,
    },
  ];
};
